import { ActionContext, ModuleTree, createStore, createLogger } from 'vuex'
import { auth } from './auth_user'
import { user } from './user'
import { company } from './company'
import localStorage from 'store'
import { RootState } from './state_models'
import { AuthUser } from '../models'
import { reputation } from './reputation'
import { reviewAggregate } from './review_aggregate'
import { reviewRequestAggregate } from './review_request_aggregate'
import { imagePreview } from './image_preview'
import { contactsPage } from './contact'
import { campaigns } from './campaigns'
import { workflows } from './workflows'
import { pipelines } from './pipelines'
import { users } from './users'
import { agencyUsers } from './agency_users'
import { locations } from './location'
import { teams } from './teams'
import { calendarProviders } from './calendar_providers'
import { calendars } from './calendars'
import { numbers } from './default_number'
import { agencyTwilio } from './agency_twilio'
import phoneCall from './phone_call'
import { funnel } from './funnel'
import { contacts } from './contacts'
import { oauth2 } from './oauth2'
import { conversation } from './conversation'
import { conversationV2 } from './conversationV2'
import affiliate from './affiliate'
import { opportunities } from './opportunities'
import { filters } from './filters'
import { defaultEmailService } from './default_email_service'
import { trigger_folder } from './trigger_folder'
import { campaign_folder } from './campaign_folder'
import { smtpServices } from './smtp_services'
import { mailgunServices } from './mailgun_services'
import { membership } from './membership'
import { locationCustomFields } from './custom_fields'
import { customValues } from './custom_values'
import { manualCallStatus } from './manual_call_status'
import { iframe } from './iframe'
import { products } from './products'
import { stripeConnect } from './stripe_connect'
import { integrations } from './integrations'
import { whatsApp } from './whatsapp'
import { LevelUpDayFlag } from './levelup_day'
import { sidebarv2 } from './sidebarv2'
import { featureFlags } from './featureFlags'
import { customObjectsStore } from './customObjectsStore'
import { twilioAccount } from './twilio_account'
import { tasks } from './tasks'
import { conversationChannels } from './conversationChannels'
import { labsFeatures } from './labsFeatures'
import { affiliateManager } from './affiliate_manager'
import { communities } from './communities'
import { onboarding } from './onboarding'
import { launchpad } from './launchpad'
import { phoneIntegration } from '../apps/phone-integration/store/phone-integration'
import { whatsAppSettings } from '../apps/phone-integration/store/whatsapp-settings'
import { regulatoryBundle } from '../apps/phone-integration/store/regulatory-bundle'
import { clientPortal } from './client_portal'
import { locationNumbers } from '../apps/phone-integration/store/location-numbers'
import { locationSetting } from './location_setting'
import { conversationAi } from './conversationAi'
import { aiAgents } from './aiAgents'
import { aiCompanyPlan } from './aiCompanyPlan'
import { aiLocationPlan } from './aiLocationPlan'
import { scoreProfiles } from './scoreProfiles'
import { yext } from './yext'
import { wordpress } from './wordpress'
import { freshchat } from './freshchat'
import { customPages } from './custom_pages'
import { customMenuLinks } from './custom_menu_links'
import { adPublishing } from './ad_publishing'
import { prospecting } from './prospecting'
import { notification } from './notification'
import { locationsMapSearch } from './locationsMapSearch'
import { seo } from './seo'
import { routerParams } from './routerParams'
import { supportPreferences } from './support_preferences'
import { marketplace } from './marketplace'
import { clearPendoSession, initializePendo, teardownPendo } from '../util/pendo'

const startCounter = async (state: RootState) => {
  const store = useStore()
  while (state.loaderCount > 0 && (state.agencyLoaderActive || state.locationLoaderActive)) {
    await new Promise(resolve => setTimeout(resolve, 1000))
    if (state.loaderCount) store.commit('setLoaderCount', state.loaderCount - 1)
  }
}

const countdownInitialValue = () => 30

const resetCounter = () => {
  const store = useStore()
  store.commit('setLoaderCount', countdownInitialValue())
}

const modules: ModuleTree<RootState> = {
  auth,
  user,
  company,
  reputation,
  reviewAggregate,
  reviewRequestAggregate,
  imagePreview,
  contactsPage,
  locations,
  teams,
  calendarProviders,
  calendars,
  numbers,
  users,
  agencyUsers,
  campaigns,
  workflows,
  pipelines,
  agencyTwilio,
  twilioAccount,
  phoneCall,
  funnel,
  contacts,
  oauth2,
  conversation,
  conversationV2,
  affiliate,
  opportunities,
  filters,
  defaultEmailService,
  trigger_folder,
  campaign_folder,
  smtpServices,
  mailgunServices,
  membership,
  locationCustomFields,
  customValues,
  manualCallStatus,
  iframe,
  products,
  stripeConnect,
  integrations,
  LevelUpDayFlag,
  sidebarv2,
  whatsApp,
  featureFlags,
  customObjectsStore,
  tasks,
  conversationChannels,
  labsFeatures,
  communities,
  onboarding,
  launchpad,
  phoneIntegration,
  whatsAppSettings,
  clientPortal,
  locationNumbers,
  locationSetting,
  conversationAi,
  aiAgents,
  aiCompanyPlan,
  aiLocationPlan,
  scoreProfiles,
  affiliateManager,
  yext,
  wordpress,
  freshchat,
  customPages,
  adPublishing,
  notification,
  regulatoryBundle,
  prospecting,
  customMenuLinks,
  locationsMapSearch,
  seo,
  routerParams,
  marketplace,
  supportPreferences,
}

const store = createStore<RootState>({
  plugins:
    process.env.NODE_ENV === 'development' && process.env.VUE_APP_ENABLE_STORE_LOGGER === 'true'
      ? [createLogger()]
      : [],
  state: {
    initialized: false,
    locationLoaderActive: false,
    agencyLoaderActive: true,
    loaderCount: countdownInitialValue(),
    user: null,
    locations: null,
    collapseSideBar: false,
    manualCollapseSidebar: false,
    isAdvancedCalendarView: false,
    twilioClientReady: false,
    menuTabs: {},
    currentContacts: 0,
  },
  strict: process.env.NODE_ENV === 'development',
  modules,
  getters: {
    getManualCollapseSidebar(state: RootState) {
      if (state.manualCollapseSidebar) return true
      else return state.collapseSideBar
    },
    getMenuTabs(state: RootState) {
      return state.menuTabs
    },
    getCurrentContacts(state: RootState) {
      return state.currentContacts
    },
    getTwilioClientReady: (state: RootState) => {
      return state.twilioClientReady
    },
  },
  mutations: {
    setLoaderCount: (state: RootState, count: number) => {
      state.loaderCount = count
    },
    setTwilioClientReady: (state: RootState, ready: boolean) => {
      state.twilioClientReady = ready
    },
    setDeviceId: (state: RootState, deviceId: string) => {
      state.deviceId = deviceId
    },
    setInitialized: (state: RootState, initialized: boolean) => {
      state.initialized = initialized
    },
    setSideBarState: (state: RootState, collapseSideBar: boolean) => {
      state.collapseSideBar = collapseSideBar
    },
    setLocationLoaderState: (state: RootState, status: boolean) => {
      resetCounter()
      startCounter(state)
      state.locationLoaderActive = status
    },
    setAgencyLoaderState: (state: RootState, status: boolean) => {
      resetCounter()
      startCounter(state)
      state.agencyLoaderActive = status
    },
    setManualCollapseSidebar: (state: RootState, manualCollapseSidebar: boolean) => {
      state.manualCollapseSidebar = manualCollapseSidebar
    },
    setIsAdvancedCalendarView: (state: RootState, isAdvancedCalendarView: boolean) => {
      state.isAdvancedCalendarView = isAdvancedCalendarView
    },
    setMenuTabs: (state: RootState, menuTabs: { [key: string]: any }) => {
      state.menuTabs = menuTabs
    },
    setCurrentContacts: (state: RootState, currentContacts: number) => {
      state.currentContacts = currentContacts
    },
  },
  actions: {
    getDeviceId: (context: ActionContext<RootState, RootState>) => {
      return new Promise(resolve => {
        if (context.state.deviceId) {
          return resolve(context.state.deviceId)
        }

        let deviceId = localStorage.get('deviceId')
        if (!deviceId) {
          const crypto = window.crypto || window.msCrypto
          deviceId = (String(1e7) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: string) => {
            const randomChars = crypto.getRandomValues(new Uint8Array(1)) as ArrayLike<number>
            return (Number(c) ^ (randomChars[0] & (15 >> (Number(c) / 4)))).toString(16)
          })
          localStorage.set('deviceId', deviceId)
        }
        context.commit('setDeviceId', deviceId)
        resolve(deviceId)
      })
    },
    init: async (context: ActionContext<RootState, RootState>) => {
      try {
        const credentials: AuthUser = new AuthUser(await context.dispatch('auth/get'))
        // context.dispatch('smtpServices/companySyncAll', credentials.companyId)
        context.dispatch('mailgunServices/companySyncAll', credentials.companyId)
      } catch {
        return
      }

      if (!context.state.initialized) {
        context.commit('setInitialized', true)
        context.dispatch('syncAll')
      }

      // Initialize pendo on app load with authenticated user
      initializePendo()
    },
    logout: (context: ActionContext<RootState, RootState>) => {
      context.commit('setInitialized', false)
      localStorage.set('recentVisitedLocations', [])
      // remove these keys since they are explicitly set
      localStorage.remove('refreshedToken')
      localStorage.remove('tokenRefreshTimestamp')

      // Clear Pendo session and teardown on logout
      clearPendoSession()
      teardownPendo()
    },
    setTwilioClientReady: (context: ActionContext<RootState, RootState>, value) => {
      context.commit('setTwilioClientReady', value)
    },
  },
})

export default store
export const useStore = () => store
